import moment from 'moment-timezone';
import {
    KIOSK_CHECKIN_MODES,
    KIOSK_CHECKOUT_MODES,
    isKioskRoute,
    isWithinWindow,
    kioskActiveSide,
    loadKioskConfig,
} from './kiosk_config';
import { markKioskInternalReturn } from './kiosk_transition';
import { forgetKioskLanguage } from './kiosk_language';

/**
 * Virtual Kiosk validation / session mixin.
 *
 * Reusable on every kiosk-mode page (home, check-in hub, check-in by
 * name / phone / reservation, checkout) — the same mixin the per-page form
 * validations will move into later.
 *
 * Operating-time rule (per spec):
 *   A guest who STARTED check-in (or check-out) before the end of the
 *   operating window must never be forced to finish — the window is only
 *   enforced when STARTING a session. So the clock runs on the screens that
 *   own a whole flow (the kiosk home screen; the check-out page, which owns
 *   the check-out hub AND the form the guest fills in after it), and the end
 *   of the window never closes the screen out from under a guest who is
 *   mid-transaction: it raises `kiosk_close_pending`, and the close happens
 *   the moment the guest is done — they finish and land back on the hub, or
 *   they walk away and the idle auto-reset fires. Either way the trip home is
 *   made with `{ closing: true }`, so the home screen announces "Now Closing".
 */
// Module scope, so every kiosk component on the page shares it: the first trip
// home wins. Two of them can decide to leave in the same tick — the check-out
// page closing for the day while the hub it just re-rendered also finds the
// window shut — and a second assignment to location.href would replace the
// first, dropping the "Now Closing" the page had already asked for.
let kioskLeavingForHome = false;

// Longest a suspension is trusted. A suspension ends on a signal from the
// payment screen (the modal hiding), and a gateway that dies mid-transaction —
// a frozen iframe, a "hidden" event that never fires — would otherwise leave a
// lobby tablet suspended for the night, holding one guest's booking on screen
// and refusing every timeout. Well past any real 3-D Secure wait, so it only
// ever fires on a screen that is already broken.
const KIOSK_MAX_SUSPEND_SECONDS = 600;

// How long the "Auto Reset" spinner stays up before the trip home starts.
// Resetting is a full page load, and until the new document paints the guest
// keeps looking at the screen they were just warned about — so the reset
// announces itself first. The wait also buys the spinner a paint of its own,
// which a navigation started in the same tick would not leave room for.
const KIOSK_AUTO_RESET_ANNOUNCE_MS = 600;

export default {
    data() {
        return {
            kiosk_config: null,
            kiosk_now: moment(),

            // Idle / auto-reset state (7. Session Management)
            kiosk_idle_warning_visible: false,
            kiosk_idle_countdown: 0,
            // The reset is under way: the warning modal has swapped to the
            // "Auto Reset" spinner and the navigation is one beat out.
            kiosk_auto_resetting: false,
            // Set while the guest is somewhere the kiosk cannot see activity
            // (the card-payment iframe), where "no events" does not mean
            // "nobody is there" — see kioskSuspendIdleWatch.
            kiosk_idle_suspended: false,
            // Suspensions nest: the payment window opens, and the processing
            // window opens on top of it while the gateway is answering. Counted
            // so the inner one closing does not restart the countdown behind a
            // payment screen that is still up.
            kiosk_idle_suspend_depth: 0,

            // The operating window ended while a guest was mid-session, so the
            // close is waiting for them to finish (see the rule at the top).
            kiosk_close_pending: false,

            kiosk_clock_timer: null,
            kiosk_idle_timer: null,
            kiosk_countdown_timer: null,
            // Screens that end a flow reset on a plain timer instead of the
            // idle watch — see kioskStartAutoReturn.
            kiosk_auto_return_timer: null,
            // Safety net behind a suspension that is never lifted — see
            // KIOSK_MAX_SUSPEND_SECONDS.
            kiosk_idle_suspend_guard: null,
        };
    },

    computed: {
        // True when this page was reached through the kiosk flow.
        isKioskMode() {
            return isKioskRoute(this.$route);
        },

        // Which side of the Guest Hub is open right now — KIOSK_MODES.CHECKIN,
        // KIOSK_MODES.CHECKOUT, or null when neither window is open. Not read
        // from the URL: there is one kiosk URL and the clock (kiosk_now, in the
        // facility timezone) picks the side from the saved operating hours, so
        // it flips by itself as time passes. See the block above KIOSK_MODES.
        kioskMode() {
            return kioskActiveSide(this.kiosk_config, this.kiosk_now);
        },

        kioskWithinCheckinHours() {
            return this.kioskIsWithinWindow(this.kiosk_config?.operating_hours?.checkin);
        },

        kioskWithinCheckoutHours() {
            return this.kioskIsWithinWindow(this.kiosk_config?.operating_hours?.checkout);
        },

        // Whether each side of the Guest Hub is switched on at all
        // (Guest Hub setting page: "Not in USE" disables the side entirely).
        kioskCheckinInUse() {
            return this.kiosk_config != null
                && this.kiosk_config.checkin_mode !== KIOSK_CHECKIN_MODES.NOT_IN_USE;
        },

        kioskCheckoutInUse() {
            return this.kiosk_config != null
                && this.kiosk_config.checkout_mode !== KIOSK_CHECKOUT_MODES.NOT_IN_USE;
        },
    },

    beforeDestroy() {
        this.kioskStopClock();
        this.kioskStopIdleWatch();
        this.kioskStopAutoReturn();
    },

    methods: {
        // Reads the settings for this page load; every open/close decision
        // afterwards is made locally against the copy stored here, so nothing
        // polls in front of a guest. The clock is re-read at the same time
        // because it is kept in the facility's timezone, which arrives with
        // the config.
        async kioskLoadConfig(facilityId) {
            this.kiosk_config = await loadKioskConfig(facilityId);
            this.kiosk_now = this.kioskCurrentTime();
            return this.kiosk_config;
        },

        // ---- Operating window validation -------------------------------

        // window: { start: 'HH:mm', end: 'HH:mm' } — supports windows that
        // wrap past midnight (e.g. 22:00 - 02:00). Empty hours are never open,
        // which is what keeps kioskCanStartCheckin/Checkout false for a side
        // that is switched on but has no hours. The math lives in kiosk_config
        // (isWithinWindow) so kioskActiveSide uses the very same rule.
        kioskIsWithinWindow(window) {
            return isWithinWindow(window, this.kiosk_now);
        },

        // Guests may only START a session inside the window; sessions in
        // progress are never interrupted (see rule at top of file).
        kioskCanStartCheckin() {
            return this.kiosk_config?.enabled && this.kioskCheckinInUse && this.kioskWithinCheckinHours;
        },

        kioskCanStartCheckout() {
            return this.kiosk_config?.enabled && this.kioskCheckoutInUse && this.kioskWithinCheckoutHours;
        },

        // Raise `kiosk_close_pending` the moment the check-in window ends, on a
        // screen a guest may still be working on. The flag is all this does:
        // when the close may actually happen is the caller's business, because
        // "the guest is finished" means something different on every screen —
        // an untouched hub can close at once, a form in progress waits for the
        // idle reset, and the completed screen rides out its own minute so the
        // last thing the guest sees is their check-in, not the closing notice.
        //
        // The check-out side keeps its own copy of this in
        // MainHotelCheckoutForm: that page holds the whole flow AND its hub, so
        // its close has one owner and no second screen to hand the flag to.
        kioskWatchCheckinWindow(onClose) {
            if (!this.kioskCanStartCheckin()) return;

            this.kioskStartClock(() => {
                if (this.kioskCanStartCheckin()) return;
                this.kiosk_close_pending = true;
                if (onClose) onClose();
            });
        },

        // ---- Clock -----------------------------------------------------

        // "Now" in the FACILITY's timezone, which is the zone the operating
        // hours are written in. Never the tablet's own clock zone: a device
        // imaged in the wrong region would open and close the hub hours off,
        // silently, with nobody standing there to notice.
        kioskCurrentTime() {
            const zone = this.kiosk_config?.timezone;
            return zone ? moment.tz(zone) : moment();
        },

        kioskStartClock(onTick) {
            this.kioskStopClock();
            this.kiosk_clock_timer = setInterval(() => {
                this.kiosk_now = this.kioskCurrentTime();
                if (onTick) onTick();
            }, 1000);

            // A tablet that went to sleep wakes with a stale clock — timers are
            // throttled or frozen while the page is hidden — so catch up before
            // the next tick decides which screen to show.
            this.kiosk_visibility_handler = () => {
                if (document.visibilityState !== 'visible') return;
                this.kiosk_now = this.kioskCurrentTime();
                if (onTick) onTick();
            };
            document.addEventListener('visibilitychange', this.kiosk_visibility_handler);
        },

        kioskStopClock() {
            if (this.kiosk_clock_timer) {
                clearInterval(this.kiosk_clock_timer);
                this.kiosk_clock_timer = null;
            }
            if (this.kiosk_visibility_handler) {
                document.removeEventListener('visibilitychange', this.kiosk_visibility_handler);
                this.kiosk_visibility_handler = null;
            }
        },

        // ---- Idle auto-reset (7. Session Management) -------------------

        // Watches guest activity; after `idle_timeout_seconds` of inactivity
        // shows the countdown warning, and when it expires returns to the
        // kiosk home screen (full navigation, so no guest data survives).
        kioskStartIdleWatch() {
            this.kioskStopIdleWatch();
            this.kiosk_activity_handler = () => this.kioskResetIdleTimer();
            ['touchstart', 'click', 'keydown', 'input'].forEach((ev) =>
                window.addEventListener(ev, this.kiosk_activity_handler, { passive: true })
            );
            this.kioskResetIdleTimer();
        },

        kioskStopIdleWatch() {
            if (this.kiosk_activity_handler) {
                ['touchstart', 'click', 'keydown', 'input'].forEach((ev) =>
                    window.removeEventListener(ev, this.kiosk_activity_handler)
                );
                this.kiosk_activity_handler = null;
            }
            if (this.kiosk_idle_timer) clearTimeout(this.kiosk_idle_timer);
            if (this.kiosk_countdown_timer) clearInterval(this.kiosk_countdown_timer);
            this.kiosk_idle_timer = null;
            this.kiosk_countdown_timer = null;
            this.kiosk_idle_warning_visible = false;
            this.kiosk_idle_suspended = false;
            this.kiosk_idle_suspend_depth = 0;
            if (this.kiosk_idle_suspend_guard) clearTimeout(this.kiosk_idle_suspend_guard);
            this.kiosk_idle_suspend_guard = null;
        },

        // Stop counting the guest as idle, without dropping the watch.
        //
        // The idle timer infers "nobody is there" from the absence of touch and
        // key events on THIS page — which is only sound while the guest is
        // typing on this page. The card-payment step hands the screen to the
        // gateway's iframe, and events inside a cross-origin frame never reach
        // us: a guest carefully typing a card number looks exactly like an
        // abandoned tablet, and the kiosk used to reset in the middle of the
        // payment. So the countdown does not run at all while the payment
        // screen is up; it picks up again when the guest comes back to a page
        // the kiosk can actually watch.
        kioskSuspendIdleWatch() {
            this.kiosk_idle_suspend_depth++;
            if (this.kiosk_idle_suspended) return;
            this.kiosk_idle_suspended = true;

            if (this.kiosk_idle_timer) clearTimeout(this.kiosk_idle_timer);
            if (this.kiosk_countdown_timer) clearInterval(this.kiosk_countdown_timer);
            this.kiosk_idle_timer = null;
            this.kiosk_countdown_timer = null;
            // A warning already on screen would sit behind the payment window
            // with a countdown nobody can answer.
            this.kiosk_idle_warning_visible = false;

            this.kiosk_idle_suspend_guard = setTimeout(
                () => this.kioskForceResumeIdleWatch(),
                KIOSK_MAX_SUSPEND_SECONDS * 1000
            );
        },

        // The payment screen never gave the signal that ends the suspension,
        // so the kiosk takes the watch back itself rather than standing open
        // indefinitely.
        kioskForceResumeIdleWatch() {
            this.kiosk_idle_suspend_depth = 1;
            this.kioskResumeIdleWatch();
        },

        kioskResumeIdleWatch() {
            if (!this.kiosk_idle_suspended) return;
            // Only the outermost suspension releases the watch: the processing
            // window closing while the payment window is still up leaves the
            // guest on a screen the kiosk still cannot see.
            this.kiosk_idle_suspend_depth = Math.max(0, this.kiosk_idle_suspend_depth - 1);
            if (this.kiosk_idle_suspend_depth > 0) return;

            if (this.kiosk_idle_suspend_guard) clearTimeout(this.kiosk_idle_suspend_guard);
            this.kiosk_idle_suspend_guard = null;
            this.kiosk_idle_suspended = false;
            // Coming back from the payment screen IS activity, so the guest
            // gets the full timeout again rather than the remainder of an old one.
            if (this.kiosk_activity_handler) this.kioskResetIdleTimer();
        },

        kioskResetIdleTimer() {
            if (this.kiosk_idle_suspended) return; // guest is on a screen we cannot watch
            if (this.kiosk_idle_warning_visible) return; // countdown owns the screen
            if (this.kiosk_idle_timer) clearTimeout(this.kiosk_idle_timer);
            const timeout = (this.kiosk_config?.idle_timeout_seconds ?? 180) * 1000;
            this.kiosk_idle_timer = setTimeout(() => {
                // Focus sitting inside a frame means the guest is right there
                // working in it, however quiet this page has been — so the
                // timeout is served again instead of the warning.
                if (this.kioskFocusIsInFrame()) return this.kioskResetIdleTimer();
                this.kioskShowIdleWarning();
            }, timeout);
        },

        // The suspend above covers the payment gateways that take over the
        // screen in a modal, which is announced to us. Card fields rendered
        // INLINE (Stripe Elements) and the 3-D Secure challenge announce
        // nothing: they are cross-origin iframes sitting in the page, and
        // every keystroke inside one stays inside it. What the browser does
        // still tell us is where focus went — an iframe becomes the
        // activeElement — so a guest typing a card number in one is not
        // mistaken for an abandoned tablet.
        kioskFocusIsInFrame() {
            return document.activeElement?.tagName === 'IFRAME';
        },

        kioskShowIdleWarning() {
            this.kiosk_idle_countdown = this.kiosk_config?.idle_warning_seconds ?? 60;
            this.kiosk_idle_warning_visible = true;
            this.kiosk_countdown_timer = setInterval(() => {
                this.kiosk_idle_countdown--;
                if (this.kiosk_idle_countdown <= 0) {
                    clearInterval(this.kiosk_countdown_timer);
                    this.kiosk_countdown_timer = null;
                    this.kioskBeginAutoReset();
                }
            }, 1000);
        },

        // ---- End-of-flow auto-return (7. Session Management) -----------

        // The completed and error screens are not screens a guest works on:
        // there is nothing to type, nothing to lose, and no question worth
        // interrupting them with — so they carry no warning at all. They simply
        // return to the start screen a minute after they appear, which is also
        // what clears the last guest's booking details off a lobby tablet
        // nobody taps "back" on (8. Privacy).
        //
        // Deliberately not re-armed by touch: the guest is finished, and the
        // fixed minute is what guarantees the screen clears itself.
        kioskStartAutoReturn(seconds) {
            this.kioskStopAutoReturn();
            const wait = (seconds ?? this.kiosk_config?.idle_auto_return_seconds ?? 60) * 1000;
            this.kiosk_auto_return_timer = setTimeout(() => {
                this.kiosk_auto_return_timer = null;
                this.kioskBeginAutoReset();
            }, wait);
        },

        kioskStopAutoReturn() {
            if (this.kiosk_auto_return_timer) clearTimeout(this.kiosk_auto_return_timer);
            this.kiosk_auto_return_timer = null;
        },

        // "つづける" / Continue — guest is still there, keep the session.
        kioskContinueSession() {
            if (this.kiosk_countdown_timer) clearInterval(this.kiosk_countdown_timer);
            this.kiosk_countdown_timer = null;
            this.kiosk_idle_warning_visible = false;
            this.kioskResetIdleTimer();
        },

        // Every automatic reset goes through here rather than calling
        // kioskAutoReset directly: it puts the "Auto Reset" spinner up first,
        // and pages that send an idle guest somewhere other than the home
        // screen (the check-out form returns them to the check-out hub) get
        // the same announcement by overriding kioskAutoReset alone.
        kioskBeginAutoReset() {
            if (this.kiosk_auto_resetting) return;
            this.kiosk_auto_resetting = true;
            // The same modal carries both, so the countdown is what gives way
            // to the spinner — the window never closes in between.
            this.kiosk_idle_warning_visible = false;
            setTimeout(() => this.kioskAutoReset(), KIOSK_AUTO_RESET_ANNOUNCE_MS);
        },

        // Return to the kiosk home screen. Full navigation (not $router.push)
        // so every bit of in-memory guest state is dropped — the home screen
        // then re-evaluates the operating window and closes if needed.
        //
        // A close that was waiting on this guest rides out with them: the
        // window ended while they were mid-session, they were allowed to
        // finish, and the trip home is what finally announces it. Without the
        // flag the home screen would land the next person on "Out of Operating
        // Time" with nothing said about why.
        kioskAutoReset() {
            this.kioskGoHome({ closing: this.kiosk_close_pending });
        },

        // `closing: true` marks the trip home as "the window just ended", so
        // the home screen announces it with the "Now Closing" transition
        // instead of the "Now Starting" one it shows on a plain boot. Also
        // used as a plain @click handler, where the event argument simply
        // reads as no options.
        kioskGoHome({ closing = false } = {}) {
            if (kioskLeavingForHome) return;
            kioskLeavingForHome = true;

            // Every trip home is a return, never a start: Back, the idle
            // auto-reset, a finished session, a window that closed. The home
            // screen reads this and skips the "Now Starting" transition it
            // plays on a boot — `closing: true` still gets its own
            // announcement, because that one is news rather than navigation.
            markKioskInternalReturn();

            // The start screen is a clean slate for the next guest, and the
            // language the last one chose is theirs, not the kiosk's. Dropping
            // it here is what puts the tablet back into Japanese between
            // guests — the same reset every other bit of session state gets.
            forgetKioskLanguage();

            // Only `closing` rides along. The side is not carried: the home
            // screen works out which one is open from the clock, so the guest
            // lands on whatever is in operation at that moment.
            const { href } = this.$router.resolve({
                name: 'checkin-facility-kiosk-home',
                params: { slug_name: this.$route.params.slug_name },
                query: closing ? { closing: 'true' } : {},
            });
            window.location.href = href;
        },
    },
};
